import { useFieldArray, useFormContext } from "react-hook-form";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, Percent, Plus, X, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface GeographicMarkupSectionProps {
  namePrefix?: string;
  session?: any;
}

// API response interfaces
interface ApiResponse<T> {
  success: boolean;
  data: T[];
}

export const GeographicMarkupSection = ({
  namePrefix = "pricing_details",
  session,
}: GeographicMarkupSectionProps) => {
  const { control, watch, setValue } = useFormContext();
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<Record<string, string[]>>({});
  const [districts, setDistricts] = useState<Record<string, string[]>>({});
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState<Record<string, boolean>>({});
  const [isLoadingDistricts, setIsLoadingDistricts] = useState<Record<string, boolean>>({});

  // Create field array for geographic markups
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${namePrefix}.geographicMarkups`,
  });

  const enableGeographicPricing = watch(
    `${namePrefix}.enableGeographicPricing`
  );
  
  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      if (!session?.access_token) return;
      
      setIsLoadingCountries(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/countries`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch countries: ${response.statusText}`);
        }
        
        const data = await response.json() as ApiResponse<string>;
        if (data.success && Array.isArray(data.data)) {
          setCountries(data.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setIsLoadingCountries(false);
      }
    };
    
    fetchCountries();
  }, [session]);
  
  // Fetch states for a country
  const fetchStates = async (country: string) => {
    if (!session?.access_token || states[country]) return;
    
    setIsLoadingStates(prev => ({ ...prev, [country]: true }));
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/states?country=${encodeURIComponent(country)}`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch states: ${response.statusText}`);
      }
      
      const data = await response.json() as ApiResponse<string>;
      if (data.success && Array.isArray(data.data)) {
        setStates(prev => ({ ...prev, [country]: data.data }));
      }
    } catch (error) {
      console.error(`Error fetching states for country ${country}:`, error);
    } finally {
      setIsLoadingStates(prev => ({ ...prev, [country]: false }));
    }
  };
  
  // Fetch districts for a state
  const fetchDistricts = async (state: string, country: string) => {
    const stateKey = `${country}:${state}`;
    if (!session?.access_token || districts[stateKey]) return;
    
    setIsLoadingDistricts(prev => ({ ...prev, [stateKey]: true }));
    try {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/npos/districts?state=${encodeURIComponent(state)}&country=${encodeURIComponent(country)}`, 
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch districts: ${response.statusText}`);
      }
      
      const data = await response.json() as ApiResponse<string>;
      if (data.success && Array.isArray(data.data)) {
        setDistricts(prev => ({ ...prev, [stateKey]: data.data }));
      }
    } catch (error) {
      console.error(`Error fetching districts for state ${state} in ${country}:`, error);
    } finally {
      setIsLoadingDistricts(prev => ({ ...prev, [stateKey]: false }));
    }
  };

  return (
    <div className="space-y-4 border p-4 rounded-md bg-background/80">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="h-5 w-5 text-primary" />
        <h2 className="text-base font-semibold">
          Geographic Pricing Adjustments
        </h2>
      </div>

      <FormField
        control={control}
        name={`${namePrefix}.enableGeographicPricing`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Enable Geographic Pricing Adjustments</FormLabel>
              <FormDescription>
                Set different pricing for different locations based on regional
                costs.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      {enableGeographicPricing && (
        <div className="space-y-4">
          {fields.map((item: any, index) => (
            <div
              key={item.id}
              className="grid grid-cols-1 gap-4 p-3 border rounded-md"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Country Selection */}
                <FormField
                  control={control}
                  name={`${namePrefix}.geographicMarkups.${index}.country`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          fetchStates(value);
                          
                          // Reset state and district when country changes
                          setValue(`${namePrefix}.geographicMarkups.${index}.state`, "");
                          setValue(`${namePrefix}.geographicMarkups.${index}.district`, "");
                          
                          // Set location ID and name based on country
                          setValue(`${namePrefix}.geographicMarkups.${index}.locationId`, value);
                          setValue(`${namePrefix}.geographicMarkups.${index}.locationName`, value);
                        }}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {isLoadingCountries ? (
                            <div className="flex items-center justify-center p-2">
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              <span>Loading countries...</span>
                            </div>
                          ) : (
                            countries.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* State Selection */}
                <FormField
                  control={control}
                  name={`${namePrefix}.geographicMarkups.${index}.state`}
                  render={({ field }) => {
                    const country = watch(`${namePrefix}.geographicMarkups.${index}.country`);
                    const statesForCountry = states[country] || [];
                    
                    return (
                      <FormItem>
                        <FormLabel>State/Province</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            fetchDistricts(value, country);
                            
                            // Reset district when state changes
                            setValue(`${namePrefix}.geographicMarkups.${index}.district`, "");
                            
                            // Update location ID and name based on state
                            setValue(`${namePrefix}.geographicMarkups.${index}.locationId`, value);
                            setValue(`${namePrefix}.geographicMarkups.${index}.locationName`, value);
                          }}
                          value={field.value || ""}
                          disabled={!country}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {isLoadingStates[country] ? (
                              <div className="flex items-center justify-center p-2">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                <span>Loading states...</span>
                              </div>
                            ) : statesForCountry.length > 0 ? (
                              statesForCountry.map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))
                            ) : (
                              <div className="p-2 text-center text-sm text-muted-foreground">
                                {country ? "No states found" : "Select a country first"}
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                {/* District/City Selection */}
                <FormField
                  control={control}
                  name={`${namePrefix}.geographicMarkups.${index}.district`}
                  render={({ field }) => {
                    const country = watch(`${namePrefix}.geographicMarkups.${index}.country`);
                    const state = watch(`${namePrefix}.geographicMarkups.${index}.state`);
                    const stateKey = `${country}:${state}`;
                    const districtsForState = districts[stateKey] || [];
                    
                    return (
                      <FormItem>
                        <FormLabel>City/District</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            
                            // Update location ID and name based on district
                            setValue(`${namePrefix}.geographicMarkups.${index}.locationId`, value);
                            setValue(`${namePrefix}.geographicMarkups.${index}.locationName`, value);
                          }}
                          value={field.value || ""}
                          disabled={!state}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select city" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {isLoadingDistricts[stateKey] ? (
                              <div className="flex items-center justify-center p-2">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                <span>Loading cities...</span>
                              </div>
                            ) : districtsForState.length > 0 ? (
                              districtsForState.map((district) => (
                                <SelectItem key={district} value={district}>
                                  {district}
                                </SelectItem>
                              ))
                            ) : (
                              <div className="p-2 text-center text-sm text-muted-foreground">
                                {state ? "No cities found" : "Select a state first"}
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
              
              {/* Hidden fields for locationId and locationName */}
              <FormField
                control={control}
                name={`${namePrefix}.geographicMarkups.${index}.locationId`}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              
              <FormField
                control={control}
                name={`${namePrefix}.geographicMarkups.${index}.locationName`}
                render={({ field }) => <input type="hidden" {...field} />}
              />

              <div className="flex items-end gap-4 mt-4">
                <FormField
                  control={control}
                  name={`${namePrefix}.geographicMarkups.${index}.markupPercentage`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Markup Percentage</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Percent className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            type="number"
                            step="0.01"
                            min="0"
                            className="pl-8"
                            value={field.value?.toString() || ""}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => remove(index)}
                  className="mb-1"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            type="button"
            onClick={() =>
              append({
                country: "",
                state: "",
                district: "",
                locationId: "",
                locationName: "",
                markupPercentage: ""
              })
            }
            className="mt-2"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Location
          </Button>
        </div>
      )}
    </div>
  );
};
